import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { QueueCountBadge } from './QueueCountBadge';

export type SortOrder = 'NEWEST' | 'OLDEST';

interface QueueTileProps {
    label: string;
    count: number | null;
    loading: boolean;
    sortOrder: SortOrder;
    onSortChange: (order: SortOrder) => void;
    onTileClick: () => void;
    thresholdLow: number;
    thresholdHigh: number;
    dataTestId?: string;
}

export const QueueTile: React.FC<QueueTileProps> = ({
    label,
    count,
    loading,
    sortOrder,
    onSortChange,
    onTileClick,
    thresholdLow,
    thresholdHigh,
    dataTestId,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const testIdBase = dataTestId ?? `queue-tile-${label.replace(/\s+/g, '-').toLowerCase()}`;

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => setAnchorEl(null);

    const handleSortSelect = (order: SortOrder) => {
        onSortChange(order);
        handleMenuClose();
    };

    return (
        <Box
            data-testid={testIdBase}
            onClick={onTileClick}
            role="button"
            tabIndex={0}
            aria-label={`View ${label} transactions`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onTileClick();
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.default',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease',
                '&:hover': {
                    boxShadow: 2,
                },
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                },
            }}
        >
            {/* Left: badge + label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {loading || count === null ? (
                    <Skeleton data-testid={`${testIdBase}-badge-skeleton`} variant="circular" width={36} height={36} />
                ) : (
                    <QueueCountBadge
                        dataTestId={`${testIdBase}-count-badge`}
                        count={count}
                        thresholdLow={thresholdLow}
                        thresholdHigh={thresholdHigh}
                    />
                )}
                {loading ? (
                    <Skeleton data-testid={`${testIdBase}-label-skeleton`} variant="text" width={160} height={24} />
                ) : (
                    <Typography
                        data-testid={`${testIdBase}-label`}
                        sx={{ fontSize: '0.95rem', fontWeight: 500, color: 'text.primary' }}
                    >
                        {label}
                    </Typography>
                )}
            </Box>

            {/* Right: sort menu trigger */}
            <IconButton
                data-testid={`${testIdBase}-sort-button`}
                size="small"
                onClick={handleMenuOpen}
                aria-label={`Sort ${label}`}
                aria-controls={menuOpen ? `sort-menu-${label}` : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                sx={{ color: 'primary.main' }}
            >
                <ArrowForwardIosIcon fontSize="small" />
            </IconButton>

            <Menu
                id={`sort-menu-${label}`}
                data-testid={`${testIdBase}-sort-menu`}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    data-testid={`${testIdBase}-sort-newest`}
                    selected={sortOrder === 'NEWEST'}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortSelect('NEWEST');
                    }}
                >
                    Sort By Newest to Oldest
                </MenuItem>
                <MenuItem
                    data-testid={`${testIdBase}-sort-oldest`}
                    selected={sortOrder === 'OLDEST'}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortSelect('OLDEST');
                    }}
                >
                    Sort By Oldest to Newest
                </MenuItem>
            </Menu>
        </Box>
    );
};
